import { ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { type SessionActivity, describeActivity, describeSessionGroup } from './test-accounts.service';

/**
 * Live-session indicator for one test account (notes/debug-wrapper-v2.md section 3).
 *
 * Awareness, not a lock: several developers in the same test account at once is normal. The pill
 * says how many live sessions exist; the list under it says what kind each is. Count and words are
 * always present, so colour is never the only signal.
 *
 * {@link part} lets a picker row place the pill and the kind list in different grid cells:
 * 'pill' renders only the pill, 'kinds' only the kind list (when live), 'all' both, stacked.
 */
@Component({
  selector: 'tb-test-account-badge',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (activity(); as a) {
      @if (part() !== 'kinds') {
        <!-- No live region: it refreshes every few seconds per account and would chatter. The
             sentence is in the accessible text instead; the visible pill is its short form. -->
        <span class="badge" [class.badge--live]="live()" [class.badge--idle]="a.status === 'ok' && a.total === 0"
          [class.badge--unknown]="a.status !== 'ok'"
          [attr.title]="summary()"
          [attr.data-testid]="'account-sessions-' + username()" [attr.data-total]="a.status === 'ok' ? a.total : null">
          <span class="sr-only">{{ summary() }}</span>
          @if (a.status !== 'ok') {
            <span aria-hidden="true">? live</span>
          } @else if (a.total === 0) {
            <span aria-hidden="true">idle</span>
          } @else {
            <span class="dot" aria-hidden="true"></span><span aria-hidden="true">{{ a.total }}{{ a.truncated ? '+' : '' }} live</span>
          }
        </span>
      }
      @if (part() !== 'pill' && showKinds() && live()) {
        <ul class="kinds" [attr.data-testid]="'account-session-kinds-' + username()" aria-hidden="true">
          @for (g of a.groups; track $index) {
            <li><span class="n">{{ g.count }}</span> {{ kind(g) }}</li>
          }
        </ul>
      }
    }
  `,
  styles: `
    :host { display: contents; }
    /* position: relative contains the absolutely positioned .sr-only text. Without it the text's
       containing block was the page, so it escaped the drawer's overflow clip and made the whole
       debug page scroll by the height of the account list. */
    .badge {
      position: relative;
      display: inline-flex; align-items: center; gap: var(--tb-space-1, 0.25rem); flex: none;
      min-height: 22px; padding: 0 var(--tb-space-2, 0.5rem); border-radius: var(--tb-radius-pill, 999px);
      font: var(--tb-type-body-small); font-weight: 600; line-height: 1; white-space: nowrap;
      border: 1px solid var(--tb-border-line, #ccc);
      color: var(--tb-text-muted, #666); background: var(--tb-surface, #fff);
    }
    .badge--idle { border-color: transparent; background: transparent; font-weight: 400; }
    .badge--live {
      color: var(--tb-text-primary, #222);
      border-color: color-mix(in srgb, var(--tb-status-info, #1f6feb) 55%, transparent);
      background: color-mix(in srgb, var(--tb-status-info, #1f6feb) 12%, var(--tb-surface, #fff));
    }
    .badge--unknown { border-style: dashed; }
    .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; border: 0; }
    .dot { width: 0.45rem; height: 0.45rem; border-radius: 50%; background: var(--tb-status-info, #1f6feb); }
    .kinds { list-style: none; margin: 0; padding: 0; font: var(--tb-type-body-small); color: var(--tb-text-muted, #666); }
    .kinds .n { font-weight: 700; color: var(--tb-text-primary, #333); font-variant-numeric: tabular-nums; }
  `,
})
export class TestAccountBadgeComponent {
  readonly username = input.required<string>();
  readonly activity = input<SessionActivity | undefined>(undefined);
  /** Render the per-kind breakdown under the pill (picker rows); the pill alone otherwise. */
  readonly showKinds = input(true);
  /** Which part to render; see the class comment. */
  readonly part = input<'all' | 'pill' | 'kinds'>('all');

  protected readonly live = computed(() => {
    const a = this.activity();
    return !!a && a.status === 'ok' && a.total > 0;
  });
  protected readonly summary = computed(() => describeActivity(this.activity()));
  protected readonly kind = describeSessionGroup;
}
